import { Navigate, NavLink, Route, Routes, useLocation } from 'react-router-dom';
import { useState } from 'react';
import { KeyRound, LogOut, Menu, X } from 'lucide-react';
import { AuthProvider, useAuth } from './components/AuthProvider';
import { McpTokenProvider } from './components/McpTokenProvider';
import { Spinner } from './components/Feedback';
import { Logo } from './components/Logo';
import { AuthPage } from './pages/AuthPage';
import { DashboardPage } from './pages/DashboardPage';
import { DatabasesPage } from './pages/DatabasesPage';
import { NewDatabasePage } from './pages/NewDatabasePage';
import { DatabaseDetailPage } from './pages/DatabaseDetailPage';
import { McpTokensPage } from './pages/McpTokensPage';
import { QueryPage } from './pages/QueryPage';

export function App() {
  return (
    <AuthProvider>
      <McpTokenProvider>
        <Shell />
      </McpTokenProvider>
    </AuthProvider>
  );
}

/** Renders children only for a signed-in user, preserving the intended path. */
function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="boot">
        <Spinner label="Starting MCPForge" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  return <>{children}</>;
}

/**
 * Renders children only for a signed-in user.
 *
 * There is deliberately no token requirement here. An account with no MCP
 * token is still perfectly able to register a database, so gating the database
 * pages behind one trapped a new user on the token page: the first thing the
 * app should ask for is a database, and the token comes after it. A user
 * without a token now lands on the dashboard, sees no token, and is told what
 * to do next instead of being redirected away from the form.
 */
function Shell() {
  const { user, signOut } = useAuth();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // The auth screens render their own full-bleed layout.
  if (location.pathname === '/login' || location.pathname === '/register') {
    return (
      <Routes>
        <Route path="/login" element={<AuthPage mode="login" />} />
        <Route path="/register" element={<AuthPage mode="register" />} />
      </Routes>
    );
  }

  return (
    <>
      <header className="topbar">
        <NavLink to="/dashboard" className="brand">
          <Logo className="logo-topbar" />
        </NavLink>

        {user && (
          <>
            <nav className="desktop-nav" aria-label="Main navigation">
              <NavLink to="/dashboard">Dashboard</NavLink>
              <NavLink to="/databases">Databases</NavLink>
              <NavLink to="/query">Query</NavLink>
              <NavLink to="/mcp-tokens">
                <KeyRound size={15} /> MCP tokens
              </NavLink>
              <span className="topbar-user">{user.name}</span>
              <button className="ghost" onClick={() => void signOut()}>
                <LogOut size={16} /> Sign out
              </button>
            </nav>
            <button
              className="mobile-menu-toggle"
              aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileMenuOpen}
              aria-controls="mobile-navigation"
              onClick={() => setMobileMenuOpen((prev: boolean) => !prev)}
            >
              {mobileMenuOpen ? <X size={18} /> : <Menu size={18} />}
            </button>
            <nav
              id="mobile-navigation"
              aria-label="Mobile navigation"
              className={mobileMenuOpen ? 'mobile-menu open' : 'mobile-menu'}
            >
              <NavLink
                to="/dashboard"
                onClick={() => setMobileMenuOpen(false)}
              >
                Dashboard
              </NavLink>
              <NavLink
                to="/databases"
                onClick={() => setMobileMenuOpen(false)}
              >
                Databases
              </NavLink>
              <NavLink to="/query" onClick={() => setMobileMenuOpen(false)}>
                Query
              </NavLink>
              <NavLink
                to="/mcp-tokens"
                onClick={() => setMobileMenuOpen(false)}
              >
                <KeyRound size={15} /> MCP tokens
              </NavLink>
              <span className="topbar-user">{user.name}</span>
              <button
                className="ghost"
                onClick={() => {
                  setMobileMenuOpen(false);
                  void signOut();
                }}
              >
                <LogOut size={16} /> Sign out
              </button>
            </nav>
          </>
        )}
      </header>

      <main>
        <Routes>
          <Route path="/login" element={<AuthPage mode="login" />} />
          <Route path="/register" element={<AuthPage mode="register" />} />
          <Route
            path="/dashboard"
            element={
              <RequireAuth>
                <DashboardPage />
              </RequireAuth>
            }
          />
          <Route
            path="/databases"
            element={
              <RequireAuth>
                <DatabasesPage />
              </RequireAuth>
            }
          />
          <Route
            path="/databases/new"
            element={
              <RequireAuth>
                <NewDatabasePage />
              </RequireAuth>
            }
          />
          <Route
            path="/databases/:id"
            element={
              <RequireAuth>
                <DatabaseDetailPage />
              </RequireAuth>
            }
          />
          <Route
            path="/query"
            element={
              <RequireAuth>
                <QueryPage />
              </RequireAuth>
            }
          />
          <Route
            path="/mcp-tokens"
            element={
              <RequireAuth>
                <McpTokensPage />
              </RequireAuth>
            }
          />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </main>
    </>
  );
}
